if (!customElements.get('quick-cart-drawer')) {
  class QuickCartDrawer extends HTMLElement {
    constructor() {
      super();
      if (Shopify.designMode) {
        window.addEventListener('shopify:section:load', this.init.bind(this));
        this.parentElement.addEventListener('shopify:section:select', () => this.open());
        this.parentElement.addEventListener('shopify:section:deselect', () => this.close());
      }
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.toggleState = false;

      if (!this.classList.contains('is--open')) {
        document.querySelector('body').classList.remove('overflow-hidden');
      }

      if (!this._bound) {
        this._bound = true;
        this.querySelector('.button--close').addEventListener('click', this.close.bind(this));
        this.querySelector('.quick-cart-drawer__backdrop').addEventListener('click', this.close.bind(this));
        this.querySelector('.quick-cart-drawer__blocks').addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.toggleState) {
          this.close();
        }

        if (this.toggleState) {
          const focusableElements = this.querySelectorAll(
            'button, [href], input, select, label, textarea, [tabindex]:not([tabindex="-1"])'
          );
          const firstFocusableElement = focusableElements[0];
          const lastFocusableElement = focusableElements[focusableElements.length - 1];

          let isTabPressed = e.key === 'Tab';

          if (!isTabPressed) {
            return;
          }

          if (e.shiftKey) {
            if (document.activeElement === firstFocusableElement) {
              e.preventDefault();
              lastFocusableElement.focus();
            }
          } else {
            if (document.activeElement === lastFocusableElement) {
              e.preventDefault();
              firstFocusableElement.focus();
            }
          }
        }
      });
      }

      this.initTriggers();
    }

    initTriggers() {
      if (this._triggersBound) return;
      this._triggersBound = true;
      this.cache = new Map();
      this.inflight = new Map();
      this.requestId = 0;

      document.addEventListener('click', event => {
        const target = event.target instanceof Element ? event.target : event.target.parentElement;
        const trigger = target?.closest('.quick-cart-drawer__trigger');
        if (!trigger || trigger.hasAttribute('disabled')) return;
        this.fetchProductForQuickCartDrawer(event);
      }, true);

      const warm = event => {
        const trigger = event.target.closest?.('.quick-cart-drawer__trigger');
        if (!trigger || trigger.hasAttribute('disabled')) return;
        clearTimeout(this.prefetchTimer);
        this.prefetchTimer = setTimeout(() => this.prefetch(trigger), 40);
      };
      document.addEventListener('pointerover', warm, { passive: true });
      document.addEventListener('touchstart', event => {
        const trigger = event.target.closest?.('.quick-cart-drawer__trigger');
        if (!trigger || trigger.hasAttribute('disabled')) return;
        this.prefetch(trigger);
      }, { passive: true });
    }

    productRequestUrl(trigger) {
      let fetchPrefix = '';
      if (window.Shopify.routes.root.includes(`/${window.Shopify.locale}/`)) {
        fetchPrefix = `/${window.Shopify.locale}`;
      }
      return `${fetchPrefix}${trigger.dataset.productUrl}/?view=quick-cart`;
    }

    prefetch(trigger) {
      const url = this.productRequestUrl(trigger);
      if (!url || this.cache.has(url) || this.inflight.has(url)) return;
      this.load(url);
    }

    load(url) {
      if (this.cache.has(url)) return Promise.resolve(this.cache.get(url));
      if (this.inflight.has(url)) return this.inflight.get(url);
      const pending = fetch(url)
        .then(response => response.text())
        .then(html => {
          this.cache.set(url, html);
          this.inflight.delete(url);
          return html;
        })
        .catch(error => {
          this.inflight.delete(url);
          throw error;
        });
      this.inflight.set(url, pending);
      return pending;
    }

    async fetchProductForQuickCartDrawer(event) {
      event.preventDefault();
      const target = event.target instanceof Element ? event.target : event.target?.parentElement;
      const trigger = target?.closest('[data-product-url]');
      if (!trigger) return;

      const isRecommendations = trigger.classList.contains('quick-cart-drawer__trigger--recommendations');
      const productCard = trigger.closest('product-card');

      const slider = trigger.closest('card-product-slider');
      if (slider?.slider?.autoplay) {
        slider.slider.autoplay.stop();
        slider.classList.add('product--open-on-quick-cart');
      }

      const requestId = ++this.requestId;
      const main = this.querySelector('.quick-cart-drawer__main');
      const url = this.productRequestUrl(trigger);

      if (this.cache.has(url)) {
        this.renderQuickCart(this.cache.get(url), productCard, isRecommendations);
        this.open();
        return;
      }

      this.open();
      main.classList.add('is--loading');
      main.innerHTML = '<div class="quick-cart-drawer__loading"><div class="lds-ring"><span></span><span></span><span></span><span></span></div></div>';

      try {
        const html = await this.load(url);
        if (requestId !== this.requestId) return;
        this.renderQuickCart(html, productCard, isRecommendations);
      } catch (error) {
        if (requestId === this.requestId) main.classList.remove('is--loading');
      }
    }

    renderQuickCart(html, sourceCard, isRecommendations) {
      const holder = document.createElement('div');
      holder.insertAdjacentHTML('beforeend', html);
      const quickCartProduct = holder.querySelector('.quick-cart-product');
      const main = this.querySelector('.quick-cart-drawer__main');
      if (!quickCartProduct) {
        main.classList.remove('is--loading');
        return;
      }

      if (this.sliderInstance) {
        this.sliderInstance.destroy(true, true);
        this.sliderInstance = null;
      }

      main.classList.remove('is--loading');
      main.innerHTML = '';
      main.append(quickCartProduct);

      const swiperEl = main.querySelector('.quick-cart-drawer__media-swiper');
      if (swiperEl && typeof Swiper !== 'undefined') {
        this.sliderInstance = new Swiper(swiperEl, {
          slidesPerView: 1.25,
          spaceBetween: 8,
          freeMode: { enabled: true },
          breakpoints: { 750: { slidesPerView: 2 } }
        });
      }

      if (!isRecommendations && sourceCard) {
        sourceCard.querySelectorAll('input[type="radio"]:checked, select').forEach(productOption => {
          let productOptionName = productOption.name;
          const optionSectionIdAttribute = productOption.dataset?.sectionId;
          if (optionSectionIdAttribute) {
            productOptionName = productOptionName.replace(optionSectionIdAttribute, '');
          }

          const quickCartDrawerOption = this.querySelector(
            `[name="${CSS.escape(productOptionName)}-quick-cart-product"][value="${CSS.escape(productOption.value)}"]`
          );
          if (!quickCartDrawerOption) return;

          this.querySelectorAll(`[name="${CSS.escape(productOptionName)}-quick-cart-product"]`).forEach(radioInput => {
            radioInput.removeAttribute('checked');
            radioInput.checked = false;
          });

          quickCartDrawerOption.setAttribute('checked', '');
          quickCartDrawerOption.checked = true;
          const legend = quickCartDrawerOption.closest('fieldset')?.querySelector('[data-selected-variant]');
          if (legend) legend.textContent = productOption.value;

          const sourceId = sourceCard.querySelector('input[name="id"]');
          const targetId = this.querySelector('product-card input[name="id"]');
          if (sourceId && targetId) targetId.value = sourceId.value;
        });
      }

      const quickCard = main.querySelector('product-card');
      if (quickCard && !quickCard.querySelector('form')?.hasListener) quickCard.init();

      main.querySelectorAll('.variant-option-radio-input').forEach(radioInput => {
        radioInput.addEventListener('change', event => {
          const legend = event.target.closest('fieldset')?.querySelector('[data-selected-variant]');
          if (legend) legend.textContent = event.target.value;
          const selectedVariantId = this.querySelector(".product-card__add-to-cart--form input[name='id']")?.value;
          const featuredMedia = this.querySelector('product-card')?.variantsObj?.find(variant => variant.id == selectedVariantId)?.featured_media;
          if (featuredMedia) this.setActiveMedia(featuredMedia.id);
        });
      });
    }

    setActiveMedia(id) {
      const mediaFound = Array.from(this.querySelectorAll('[data-media-id]')).find(media => Number(media.dataset.mediaId) === id);
      if (mediaFound) {
        this.sliderInstance.slideTo(Number(mediaFound.dataset.index));
      }
    }

    /** Drawer coreß functions */
    toggle() {
      if (!this.toggleState) {
        this.open();
      } else {
        this.close();
      }
    }

    syncViewport() {
      const vv = window.visualViewport;
      if (!vv) return;
      const gap = Math.max(0, Math.round(window.innerHeight - vv.offsetTop - vv.height));
      this.style.setProperty('--vv-bottom', gap + 'px');
    }

    bindViewport() {
      this.onViewportChange = this.syncViewport.bind(this);
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.onViewportChange);
        window.visualViewport.addEventListener('scroll', this.onViewportChange);
      }
      this.syncViewport();
    }

    unbindViewport() {
      if (window.visualViewport && this.onViewportChange) {
        window.visualViewport.removeEventListener('resize', this.onViewportChange);
        window.visualViewport.removeEventListener('scroll', this.onViewportChange);
      }
      this.style.removeProperty('--vv-bottom');
    }

    open() {
      this.toggleState = true;
      document.querySelector('body').classList.add('overflow-hidden');
      this.bindViewport();
      const quickCartDrawer = document.querySelector('.quick-cart-drawer__blocks');
      const closeButton = quickCartDrawer.querySelector('.button--close');
      quickCartDrawer.setAttribute('tabindex', '0');
      closeButton.setAttribute('tabindex', '0');
      this.classList.add('is--open');
      this.opened();

      const firstFocusableElement = this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
    }

    close() {
      this.toggleState = false;

      const shopTheLookDrawer = document.querySelector('shop-the-look-drawer');

      if (shopTheLookDrawer && !shopTheLookDrawer.classList.contains('is--open')) {
        document.querySelector('body').classList.remove('overflow-hidden');
      }
      if (!shopTheLookDrawer) {
        document.querySelector('body').classList.remove('overflow-hidden');
      }
      this.classList.remove('is--open');
      this.unbindViewport();
      this.closed();
      this.toggleAriaExpanded();

      /** start autoplay on quick cart drawe close */
      if (document.querySelector('card-product-slider.product--open-on-quick-cart')) {
        document.querySelector('card-product-slider.product--open-on-quick-cart').slider.autoplay.start();
        document.querySelector('card-product-slider.product--open-on-quick-cart').classList.remove('product--open-on-quick-cart');
      }
    }

    toggleAriaExpanded(event) {
      if (event) {
        if (event.target.closest('button')) event.target.closest('button').setAttribute('aria-expanded', true);
        this.querySelector('.button--close').setAttribute('aria-expanded', true);
      } else {
        document.querySelectorAll('[aria-controls="quick-cart-drawer"]').forEach(button => {
          button.setAttribute('aria-expanded', false);
        });
      }
    }

    opened() {
      const openedEvent = new Event('opened', { bubbles: true });
      this.dispatchEvent(openedEvent);
    }

    closed() {
      const closedEvent = new Event('closed', { bubbles: true });
      this.dispatchEvent(closedEvent);
    }
  }

  customElements.define('quick-cart-drawer', QuickCartDrawer);
}
